import React from 'react';

const TruckTurnaroundCard = ({ data = [], loading = false, width = '100%', height = 'auto', maxWidth = '416px', maxHeight = '287px' }) => {
  const first = data?.[0] || {};
  const trucks = first?.trucks || [];
  const totalTrucks = Number(first?.trucks_unloaded ?? trucks.length) || 0;

  const sum = (arr, key) => arr.reduce((s, x) => s + (Number(x?.[key]) || 0), 0);
  const totalTat = sum(trucks, 'unloading_tat');
  const avgTat = trucks.length ? Math.round(totalTat / trucks.length) : 0; // minutes

  const formatTime = (mins) => {
    const m = Math.max(0, Math.round(mins || 0));
    const h = Math.floor(m / 60);
    const r = m % 60;
    return h ? `${h}h ${r} min` : `${r} min`;
  };

  /** --------- SVGs --------- */
  const TruckIconSVG = ({ size = 52 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 52 52" fill="none">
      <path
        d="M7.3125 9.75C5.06783 9.75 3.25 11.57 3.25 13.8125V29.25H29.25V13.8125C29.25 11.5678 27.43 9.75 25.1875 9.75H7.3125ZM29.25 32.5H3.25V38.1875C3.25 40.43 5.07 42.25 7.3125 42.25H8.125C8.125 40.5261 8.80982 38.8728 10.0288 37.6538C11.2478 36.4348 12.9011 35.75 14.625 35.75C16.3489 35.75 18.0022 36.4348 19.2212 37.6538C20.4402 38.8728 21.125 40.5261 21.125 42.25H27.625C28.056 42.25 28.4693 42.0788 28.774 41.7741C29.0788 41.4693 29.25 41.056 29.25 40.625V32.5Z"
        fill="#718EBF"
      />
      <path
        d="M17.875 42.25C17.875 41.3881 17.5326 40.5614 16.9231 39.9519C16.3136 39.3424 15.487 39 14.625 39C13.763 39 12.9364 39.3424 12.3269 39.9519C11.7174 40.5614 11.375 41.3881 11.375 42.25C11.375 43.112 11.7174 43.9386 12.3269 44.5481C12.9364 45.1576 13.763 45.5 14.625 45.5C15.487 45.5 16.3136 45.1576 16.9231 44.5481C17.5326 43.9386 17.875 43.112 17.875 42.25ZM34.125 14.625C33.694 14.625 33.2807 14.7962 32.976 15.101C32.6712 15.4057 32.5 15.819 32.5 16.25V40.625C32.5 40.8128 32.5303 40.9919 32.591 41.1624C32.861 39.5615 33.7204 38.1197 34.9999 37.1205C36.2794 36.1213 37.8866 35.6372 39.5051 35.7632C41.1237 35.8893 42.6365 36.6164 43.7459 37.8016C44.8553 38.9869 45.481 40.5444 45.5 42.1677C47.3482 41.7885 48.7977 40.1419 48.672 38.0879C48.1872 30.1543 45.3747 22.5397 40.586 16.1959C40.2126 15.7057 39.7307 15.3088 39.1781 15.0362C38.6254 14.7637 38.0172 14.6229 37.401 14.625H34.125Z"
        fill="#718EBF"
      />
      <path
        d="M42.25 42.25C42.25 41.388 41.9076 40.5614 41.2981 39.9519C40.6886 39.3424 39.862 39 39 39C38.138 39 37.3114 39.3424 36.7019 39.9519C36.0924 40.5614 35.75 41.388 35.75 42.25C35.75 43.112 36.0924 43.9386 36.7019 44.5481C37.3114 45.1576 38.138 45.5 39 45.5C39.862 45.5 40.6886 45.1576 41.2981 44.5481C41.9076 43.9386 42.25 43.112 42.25 42.25Z"
        fill="#718EBF"
      />
    </svg>
  );

  /** --------- Skeleton --------- */
  const SkeletonLoader = () => (
    <>
      <div
        style={{
          width: '100%',
          height: '90px',
          borderRadius: '16px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))',
          outline: '1px solid rgba(120,153,255,0.15)',
          marginBottom: '20px'
        }}
      />
      <div
        style={{
          width: '24%',
          height: '26px',
          backgroundColor: 'rgba(255,255,255,0.10)',
          borderRadius: '6px',
          marginBottom: '10px'
        }}
      />
      <div
        style={{
          width: '36%',
          height: '22px',
          backgroundColor: 'rgba(255,255,255,0.08)',
          borderRadius: '6px',
          marginBottom: '10px'
        }}
      />
      <div
        style={{
          width: '30%',
          height: '22px',
          backgroundColor: 'rgba(255,255,255,0.08)',
          borderRadius: '6px'
        }}
      />
    </>
  );

  /** --------- Pill Header --------- */
  const PillHeader = () => (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: 'clamp(80px, 12vw, 90px)',
        borderRadius: '16px',
        padding: 'clamp(12px, 3vw, 16px)',
        display: 'flex',
        alignItems: 'center',
        color: '#fff',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, rgba(20,31,74,0.9) 0%, rgba(12,18,48,0.9) 100%)',
        boxShadow: 'inset 0 0 0 1px rgba(86,129,255,0.55), 0 8px 22px rgba(0,0,0,0.35)',
        marginBottom: 'clamp(16px, 3vw, 20px)'
      }}
    >
      {/* slanted dark overlay */}
      <div
        style={{
          position: 'absolute',
          right: '-20%',
          top: 0,
          bottom: 0,
          width: '70%',
          transform: 'skewX(-18deg)',
          background: 'linear-gradient(180deg, rgba(7,12,32,0.65) 0%, rgba(7,12,32,0.2) 100%)',
          filter: 'blur(0.2px)'
        }}
      />

      {/* LEFT: Title, chip and total count */}
      <div style={{ position: 'relative', zIndex: 2, flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 8
          }}
        >
          <div style={{ 
            fontSize: 'clamp(14px, 2.5vw, 16px)', 
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            Dock Utilization
          </div>

          {/* green chip */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 10px',
              borderRadius: 999,
              fontSize: 'clamp(9px, 1.8vw, 11px)',
              fontWeight: 700,
              color: '#22c55e',
              background: 'rgba(34,197,94,0.15)',
              border: '1px solid rgba(34,197,94,0.35)',
              flexShrink: 0
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#22c55e',
                display: 'inline-block'
              }}
            />
            {avgTat} Mins
          </div>
        </div>

        <div style={{ 
          display: 'flex', 
          alignItems: 'baseline', 
          gap: 8,
          minWidth: 0
        }}>
          <div style={{ 
            fontSize: 'clamp(28px, 6vw, 36px)', 
            fontWeight: 300, 
            lineHeight: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {totalTrucks}
          </div>
          <div style={{ 
            fontSize: 'clamp(10px, 2vw, 12px)', 
            opacity: 0.8,
            flexShrink: 0
          }}>
            Total Trucks
          </div>
        </div>
      </div>

      {/* RIGHT: single truck icon */}
      <div
        style={{
          position: 'absolute',
          right: 'clamp(12px, 3vw, 16px)',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 3,
          flexShrink: 0
        }}
      >
        <TruckIconSVG size={window.innerWidth < 480 ? 32 : 40} />
      </div>
    </div>
  );

  /** --------- Component --------- */
  return (
    <div
      style={{
        position: 'relative',
        width: width,
        height: height,
        maxWidth: maxWidth,
        maxHeight: maxHeight,
        minWidth: '280px',
        borderRadius: '20px',
        background: 'linear-gradient(127deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)',
        backdropFilter: 'blur(60px)',
        padding: 'clamp(16px, 4vw, 20px)',
        color: '#fff',
        overflow: 'hidden',
        boxShadow: '0 12px 28px rgba(0,0,0,0.45)',
        minHeight: '200px'
      }}
    >
      {loading ? (
        <SkeletonLoader />
      ) : (
        <>
          {/* Header pill */}
          <PillHeader />

          {/* STATISTICS list */}
          <div style={{ marginTop: 0 }}>
            <div
              style={{
                fontSize: 'clamp(10px, 2vw, 12px)',
                letterSpacing: '1.4px',
                color: 'rgba(226,232,240,0.75)',
                marginBottom: 'clamp(12px, 2.5vw, 16px)'
              }}
            >
              STATISTICS
            </div>

            <div
              style={{
                maxHeight: 'clamp(100px, 18vw, 120px)',
                overflowY: 'auto',
                paddingRight: 8,
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
              className="no-scrollbar"
            >
              {trucks.map((truck, idx) => (
                <div key={idx} style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 10
                    }}
                  >
                    <div
                      style={{
                        width: 'clamp(32px, 5vw, 36px)',
                        height: 'clamp(32px, 5vw, 36px)',
                        borderRadius: '50%',
                        background: 'rgba(86,129,255,0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        outline: '1px solid rgba(86,129,255,0.35)',
                        flexShrink: 0
                      }}
                    >
                      <TruckIconSVG size={20} color="#AEC8FF" />
                    </div>
                    <div style={{ 
                      fontSize: 'clamp(16px, 3vw, 18px)', 
                      fontWeight: 700,
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      Truck {idx + 1}
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 12,
                      alignItems: 'end',
                      marginLeft: '48px'
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 'clamp(11px, 2vw, 13px)',
                          color: 'rgba(226,232,240,0.7)',
                          marginBottom: 6
                        }}
                      >
                        Unloading TAT
                      </div>
                      <div style={{ 
                        fontSize: 'clamp(16px, 3vw, 18px)', 
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {formatTime(truck?.unloading_tat)}
                      </div>
                    </div>

                    <div style={{ 
                      textAlign: 'right',
                      minWidth: 0
                    }}>
                      <div
                        style={{
                          fontSize: 'clamp(11px, 2vw, 13px)',
                          color: 'rgba(226,232,240,0.7)',
                          marginBottom: 6
                        }}
                      >
                        Idle Minutes
                      </div>
                      <div style={{ 
                        fontSize: 'clamp(16px, 3vw, 18px)', 
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {formatTime(truck?.idle_time)}
                      </div>
                    </div>
                  </div>

                  {idx < trucks.length - 1 && (
                    <div
                      style={{
                        margin: '16px 0',
                        height: 1,
                        backgroundColor: 'rgba(255,255,255,0.06)'
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* soft radial glow */}
      <div
        style={{
          position: 'absolute',
          top: '-40%',
          right: '-20%',
          width: 360,
          height: 360,
          borderRadius: '50%',
          background: 'radial-gradient(closest-side, rgba(255,255,255,0.06), rgba(255,255,255,0))',
          pointerEvents: 'none',
          filter: 'blur(2px)'
        }}
      />
    </div>
  );
};

export default TruckTurnaroundCard;
